import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Upload,
  FileSpreadsheet,
  CheckCircle2,
  AlertCircle,
  Download,
  RefreshCw,
  Database,
  FileText,
  HelpCircle
} from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DataUpload() {
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [mappedData, setMappedData] = useState([]);
  const [stores, setStores] = useState([]);
  const [weekEnding, setWeekEnding] = useState('');
  const fileInputRef = useRef(null);

  // Fetch stores on mount
  useEffect(() => {
    axios.get(`${API_URL}/stores`).then(res => setStores(res.data)).catch(() => {});
  }, []);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    
    try {
      const data = await readExcelFile(selectedFile);
      setPreview(data.slice(0, 10)); // Show first 10 rows
      
      // Try to auto-map the data to KPI fields
      const mapped = mapDataToKPI(data);
      setMappedData(mapped);
      
      toast.success(`Loaded ${data.length} rows from ${selectedFile.name}`);
    } catch (error) {
      console.error('Error reading file:', error);
      toast.error('Failed to read file. Please ensure it\'s a valid Excel or CSV file.');
    }
  };

  const readExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
          
          // Convert to objects using first row as headers
          const headers = jsonData[0];
          const rows = jsonData.slice(1).map(row => {
            const obj = {};
            headers.forEach((header, i) => {
              obj[header] = row[i];
            });
            return obj;
          }).filter(row => Object.values(row).some(v => v !== undefined && v !== ''));
          
          resolve(rows);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  const mapDataToKPI = (data) => {
    // Common field mappings for QSS/Clearview exports
    const fieldMappings = {
      'Store': ['Store', 'Store Name', 'Location', 'Restaurant'],
      'Net Sales': ['Net Sales', 'Net_Sales', 'Sales', 'Total Sales', 'Gross Sales'],
      'Labor %': ['Labour %', 'Labor %', 'Labor', 'Labour', 'Labor Percent'],
      'Food %': ['Food Waste %', 'Food %', 'Food Cost', 'Food Cost %', 'COGS'],
      'Discount %': ['Discount %', 'Discount', 'Discounts'],
      'Customer Count': ['Cust. Count', 'Customer Count', 'Customers', 'Trans Count', 'Transactions'],
      'Avg Sale': ['Avg. Sale', 'Avg Sale', 'Average Check', 'Avg Check'],
      'Drive Thru %': ['Drive Thru %', 'DT %', 'Drive Thru'],
      'SPLH': ['SPLH %', 'SPLH', 'Sales Per Labor Hour']
    };

    return data.map(row => {
      const mapped = {};
      
      // Find matching fields
      Object.entries(fieldMappings).forEach(([kpiField, possibleNames]) => {
        for (const name of possibleNames) {
          if (row[name] !== undefined) {
            mapped[kpiField] = row[name];
            break;
          }
        }
      });
      
      return mapped;
    }).filter(row => Object.keys(row).length > 0);
  };

  const handleImport = async () => {
    if (mappedData.length === 0) {
      toast.error('No data to import');
      return;
    }

    if (!weekEnding) {
      toast.error('Please select a week ending date');
      return;
    }

    setUploading(true);

    try {
      // Prepare bulk upload data
      const uploadData = [];
      
      for (const row of mappedData) {
        // Find store by name
        const storeName = row['Store'] || '';
        const store = stores.find(s => 
          s.name.toLowerCase().includes(storeName.toLowerCase()) ||
          storeName.toLowerCase().includes(s.name.toLowerCase())
        );

        if (!store) {
          console.warn(`Store not found: ${storeName}`);
          continue;
        }

        // Parse numeric values
        const parseNum = (val) => {
          if (typeof val === 'number') return val;
          if (typeof val === 'string') {
            const cleaned = val.replace(/[$,%]/g, '').trim();
            return parseFloat(cleaned) || 0;
          }
          return 0;
        };

        uploadData.push({
          store_id: store.id,
          week_ending: weekEnding,
          net_sales: parseNum(row['Net Sales']),
          net_sales_ly: parseNum(row['Net Sales']) * 0.9,
          discount_percent: parseNum(row['Discount %']),
          labor_percent: parseNum(row['Labor %']),
          food_percent: parseNum(row['Food %']),
          paper_percent: 3.5,
          oil_percent: 0.3,
          service_time_dt: 85,
          service_time_inside: 150,
          cash_variance: 0,
          transaction_count: parseNum(row['Customer Count']) || null,
          avg_check: parseNum(row['Avg Sale']) || null,
          notes: `Imported from ${file.name}`
        });
      }

      if (uploadData.length === 0) {
        toast.error('No matching stores found in the uploaded data');
        setUploading(false);
        return;
      }

      const response = await axios.post(`${API_URL}/kpi/upload`, { data: uploadData });
      
      if (response.data.imported > 0) {
        toast.success(`Successfully imported ${response.data.imported} store records`);
      }
      if (response.data.failed > 0) {
        toast.warning(`Failed to import ${response.data.failed} records`);
      }

      // Reset form
      setFile(null);
      setPreview(null);
      setMappedData([]);
      setWeekEnding('');
      if (fileInputRef.current) fileInputRef.current.value = '';

    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.detail || 'Failed to import data');
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const template = [
      ['Store', 'Net Sales', 'Labor %', 'Food %', 'Discount %', 'Customer Count', 'Avg Sale', 'Drive Thru %'],
      ['McAllister Place', '22662.92', '26.9', '27.5', '1.68', '1250', '18.13', '45'],
      ['Hampton Road', '28450.00', '21.3', '31.2', '2.10', '1520', '18.72', '62'],
    ];
    
    const ws = XLSX.utils.aoa_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'KPI Data');
    XLSX.writeFile(wb, 'KPI_Import_Template.xlsx');
    toast.success('Template downloaded');
  };

  if (!['owner', 'area_manager', 'manager'].includes(user?.role)) {
    return (
      <div className="text-center py-12">
        <Upload className="w-12 h-12 mx-auto mb-4 text-slate-300" />
        <h2 className="text-lg font-bold text-slate-900 mb-2">Access Restricted</h2>
        <p className="text-slate-600">Only authorized users can upload data</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="data-upload">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg p-6 text-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-3" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <Database className="w-8 h-8" />
              Data Import
            </h1>
            <p className="text-blue-100">Upload weekly KPI data from QSS/Clearview exports</p>
          </div>
          <Button onClick={downloadTemplate} variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 rounded-sm">
            <Download className="w-4 h-4 mr-2" />
            Download Template
          </Button>
        </div>
      </div>

      {/* Instructions */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex gap-3">
            <HelpCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-semibold mb-1">How to import data from QSS/Clearview:</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>Export your weekly report from QSS/Clearview as Excel (.xlsx) or CSV</li>
                <li>Make sure the file includes columns: Store Name, Net Sales, Labor %, Food %, etc.</li>
                <li>Upload the file below and select the week ending date</li>
                <li>Review the preview and click "Import Data" to add to your KPI dashboard</li>
              </ol>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Upload Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-white border border-slate-200 rounded-sm">
          <CardHeader>
            <CardTitle className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Upload File
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div 
              className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-blue-500 transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileSelect}
                className="hidden"
              />
              <FileSpreadsheet className="w-12 h-12 mx-auto text-slate-400 mb-3" />
              <p className="text-slate-600 mb-1">
                {file ? file.name : 'Click to upload or drag and drop'}
              </p>
              <p className="text-sm text-slate-400">Excel (.xlsx, .xls) or CSV files</p>
            </div>

            <div className="space-y-2">
              <Label>Week Ending Date *</Label>
              <Input
                type="date"
                value={weekEnding}
                onChange={(e) => setWeekEnding(e.target.value)}
                className="rounded-sm"
              />
            </div>

            <Button
              onClick={handleImport}
              disabled={!file || !weekEnding || uploading || mappedData.length === 0}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-sm"
            >
              {uploading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Import Data ({mappedData.length} rows)
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Preview Section */}
        <Card className="bg-white border border-slate-200 rounded-sm">
          <CardHeader>
            <CardTitle className="text-lg font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <FileText className="w-5 h-5 text-blue-600" />
              Data Preview
            </CardTitle>
          </CardHeader>
          <CardContent>
            {mappedData.length > 0 ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="font-medium">{mappedData.length} rows detected</span>
                </div>
                <div className="overflow-x-auto max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-slate-100">
                      <tr>
                        {Object.keys(mappedData[0] || {}).map((key, i) => (
                          <th key={i} className="text-left py-2 px-2 font-semibold text-slate-600">{key}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {mappedData.slice(0, 10).map((row, i) => (
                        <tr key={i} className="border-b border-slate-100">
                          {Object.values(row).map((val, j) => (
                            <td key={j} className="py-2 px-2">{String(val)}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {mappedData.length > 10 && (
                  <p className="text-sm text-slate-500">...and {mappedData.length - 10} more rows</p>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Upload a file to see preview</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Imports */}
      <Card className="bg-white border border-slate-200 rounded-sm">
        <CardHeader>
          <CardTitle className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Supported Export Formats
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <h4 className="font-semibold text-slate-900 mb-2">QSS/Clearview</h4>
              <p className="text-sm text-slate-600">Export from Reports → Executive Summary → Download Excel</p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg">
              <h4 className="font-semibold text-slate-900 mb-2">Manual Template</h4>
              <p className="text-sm text-slate-600">Download our template and fill in your weekly data manually</p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg">
              <h4 className="font-semibold text-slate-900 mb-2">Custom CSV</h4>
              <p className="text-sm text-slate-600">Any CSV with Store, Net Sales, Labor %, Food % columns</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
